import { Card, Title, Text } from '@nimbus-ds/components'
import { DotGridChart, type DotSegment } from './DotGridChart'

export interface VisitorSegments {
  /** 'ga4-engagement-v1' | 'legacy' | 'mixed' — segmentation basis of the window's snapshots */
  definition?: string
  total: number
  bouncers: { count: number; percentage: number }
  browsers: { count: number; percentage: number }
  shoppers: { count: number; percentage: number }
  buyers: { count: number; percentage: number }
}

interface VisitorSegmentationProps {
  segments: VisitorSegments | null | undefined
  isLoading?: boolean
}

const SEGMENT_CONFIG = [
  { key: 'bouncers', label: 'Rebotaron', color: '#9ca3af', description: 'Se fueron en menos de 10 segundos, sin interactuar' },
  { key: 'browsers', label: 'Navegaron', color: '#facc15', description: 'Interactuaron sin ver productos ni categorias' },
  { key: 'shoppers', label: 'Compraron con los ojos', color: '#16a34a', description: 'Vieron categorias y productos' },
  { key: 'buyers', label: 'Compraron', color: '#0ea5e9', description: 'Completaron una compra' },
] as const

export function VisitorSegmentation({ segments, isLoading }: VisitorSegmentationProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <Card.Header>
          <Title as="h3">Segmentacion de Visitantes</Title>
          <Text>Clasificacion del trafico por comportamiento</Text>
        </Card.Header>
        <Card.Body>
          <div className="flex h-[300px] items-center justify-center">
            <div className="h-8 w-48 animate-pulse rounded bg-muted" />
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (!segments || segments.total === 0) {
    return (
      <Card className="h-full">
        <Card.Header>
          <Title as="h3">Segmentacion de Visitantes</Title>
          <Text>Clasificacion del trafico por comportamiento</Text>
        </Card.Header>
        <Card.Body>
          <div className="flex h-[300px] items-center justify-center">
            <Text>Sin datos de segmentacion disponibles</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const dotSegments: DotSegment[] = SEGMENT_CONFIG.map(cfg => ({
    key: cfg.key,
    label: cfg.label,
    percentage: segments[cfg.key as keyof typeof segments]
      ? (segments[cfg.key as keyof typeof segments] as { percentage: number }).percentage
      : 0,
    color: cfg.color,
  }))

  return (
    <Card className="h-full">
      <Card.Header>
        <Title as="h3">Segmentacion de Visitantes</Title>
        <Text>
          Cada punto = 1% del trafico ({segments.total.toLocaleString()} sesiones totales)
        </Text>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col items-center gap-6">
          {/* Dot Grid */}
          <DotGridChart segments={dotSegments} />

          {/* Legend — 2 columns so 4 segments fit in 2 compact rows */}
          <div className="grid w-full max-w-lg grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {SEGMENT_CONFIG.map(cfg => {
              const seg = segments[cfg.key as keyof typeof segments] as { count: number; percentage: number }
              return (
                <div key={cfg.key} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full shrink-0"
                    style={{ backgroundColor: cfg.color }}
                  />
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <Text as="span" fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                        {seg.count > 0 && seg.percentage < 1 ? '<1%' : `${seg.percentage}%`}
                      </Text>
                      <Text as="span" fontWeight="medium" color="neutral-textHigh">{cfg.label}</Text>
                    </div>
                    <Text fontSize="caption" lineClamp={1}>{cfg.description}</Text>
                    <Text fontSize="caption">{seg.count.toLocaleString()} sesiones</Text>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
